import {
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IAttachmentProps from "./type";
import type {
    AttachmentContextType
} from "./type";
import stylesheet, {
    getAttachmentMediaRadius,
    getAttachmentRadius,
    getAttachmentColors,
    getAttachmentSize,
    useStyles
} from "./stylesheet";
import AttachmentAction from "./components/action";
import AttachmentActions from "./components/actions";
import AttachmentContent from "./components/content";
import AttachmentDescription from "./components/description";
import AttachmentGrid from "./components/grid";
import AttachmentGroup from "./components/group";
import AttachmentMedia from "./components/media";
import AttachmentTitle from "./components/title";
import AttachmentTrigger from "./components/trigger";
import {
    useAttachmentGroupContext,
    AttachmentContext
} from "./context";
import {
    getAttachmentIcon
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    RefreshCw as RefreshCwIcon,
    X as XIcon
} from "lucide-react-native";

const Attachment: FC<IAttachmentProps> = ({
    orientation = "horizontal",
    isInField: isInFieldProp,
    uploadState = "done",
    size = "medium",
    customLocalize,
    customTheme,
    description,
    errorText,
    children,
    onRemove,
    progress,
    onPress,
    onRetry,
    source,
    badge,
    style,
    title,
    icon,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const groupContext = useAttachmentGroupContext();

    const isInField = isInFieldProp ?? groupContext?.isInField ?? false;

    const currentSize = useMemo(() => getAttachmentSize({
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    }), [
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    ]);

    const currentColors = useMemo(() => getAttachmentColors({
        uploadState,
        colors
    }), [
        uploadState,
        colors
    ]);

    const borderRadius = getAttachmentRadius({
        controlPaddings,
        isInField,
        radiuses,
        borders,
        spaces
    });

    const mediaBorderRadius = getAttachmentMediaRadius({
        borderRadius,
        radiuses,
        borders,
        spaces
    });

    const {
        container: containerDynamicStyle,
        footer: footerDynamicStyle
    } = useStyles({
        controlPaddings,
        controlHeights,
        currentColors,
        shapeConfigs,
        borderRadius,
        orientation,
        shadowAlpha,
        currentSize,
        uploadState,
        elevations,
        borders,
        colors,
        spaces
    });

    const context = useMemo((): AttachmentContextType => ({
        icon: getAttachmentIcon({
            title,
            icon
        }),
        mediaBorderRadius,
        customLocalize,
        currentColors,
        borderRadius,
        orientation,
        description,
        currentSize,
        uploadState,
        customTheme,
        errorText,
        progress,
        onRemove,
        onRetry,
        onPress,
        source,
        badge,
        title,
        size
    }), [
        mediaBorderRadius,
        customLocalize,
        currentColors,
        borderRadius,
        orientation,
        description,
        currentSize,
        uploadState,
        customTheme,
        errorText,
        progress,
        onRemove,
        onRetry,
        onPress,
        source,
        badge,
        title,
        icon,
        size
    ]);

    const isInProgress = uploadState === "uploading" || uploadState === "processing";

    const renderActions = () => {
        const isShowRetry = uploadState === "error" && !!onRetry;

        if(!isShowRetry && !onRemove) {
            return null;
        }

        return <AttachmentActions>
            {
                isShowRetry ?
                    <AttachmentAction
                        accessibilityLabel={localize("retry-upload", [
                            title ?? ""
                        ])}
                        icon={RefreshCwIcon}
                        onPress={onRetry}
                    />
                :
                    null
            }
            {
                onRemove ?
                    <AttachmentAction
                        accessibilityLabel={localize(isInProgress ? "cancel-upload" : "remove-attachment", [
                            title ?? ""
                        ])}
                        onPress={onRemove}
                        icon={XIcon}
                    />
                :
                    null
            }
        </AttachmentActions>;
    };

    const renderDefaultLayout = () => {
        const renderContent = () => {
            return <AttachmentContent>
                <AttachmentTitle />
                <AttachmentDescription />
            </AttachmentContent>;
        };

        if(orientation === "vertical") {
            return <>
                <AttachmentMedia />
                <View
                    style={[
                        stylesheet.footer,
                        footerDynamicStyle
                    ]}
                >
                    {renderContent()}
                    {renderActions()}
                </View>
                {
                    uploadState === "done" && onPress ?
                        <AttachmentTrigger />
                    :
                        null
                }
            </>;
        }

        return <>
            <AttachmentMedia />
            {renderContent()}
            {renderActions()}
            {
                uploadState === "done" && onPress ?
                    <AttachmentTrigger />
                :
                    null
            }
        </>;
    };

    return <AttachmentContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                orientation === "vertical" ? stylesheet.vertical : stylesheet.horizontal,
                containerDynamicStyle
            ]}
            aria-busy={isInProgress}
        >
            {children ?? renderDefaultLayout()}
        </View>
    </AttachmentContext.Provider>;
};
export default Object.assign(Attachment, {
    Description: AttachmentDescription,
    Actions: AttachmentActions,
    Content: AttachmentContent,
    Trigger: AttachmentTrigger,
    Action: AttachmentAction,
    Group: AttachmentGroup,
    Media: AttachmentMedia,
    Title: AttachmentTitle,
    Grid: AttachmentGrid
});
